import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";

import { Observable, throwError, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { CorrelationIdService, DffError, SessionIdService } from "@dffedb/util";
import { LogService } from "../log/log.service";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "../app-shell/app-shell-layout.token";
import { buildAbsoluteLoginUrlFromHref, isLoginRoutePath, isTeknikerContext, LOGIN_ROUTE } from "../util";

@Injectable()
export class EforsyningHttpInterceptor implements HttpInterceptor {
    private static hasShownUnauthorizedAlert = false;

    constructor(
        private readonly sessionIdService: SessionIdService,
        private readonly correlationIdService: CorrelationIdService,
        private readonly logService: LogService,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly appShellLayout: AppShellLayoutOptions | null
    ) {}

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const startTime = new Date();
        const sessionId = this.sessionIdService.hentId();
        const correlationId = this.correlationIdService.genererId();

        if (this.erSammeOrigin(request.url)) {
            request = request.clone({
                setHeaders: {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    "X-Session-ID": sessionId,
                    "X-Correlation-ID": correlationId
                    /* eslint-enable @typescript-eslint/naming-convention */
                }
            });
        }

        this.safe(() => this.logStart(correlationId, request));
        return next.handle(request).pipe(
            tap((response) => {
                this.safe(() => this.logEnd(correlationId, startTime, request, response));
            }),
            catchError((error) => {
                this.safe(() => this.logError(correlationId, startTime, request, error));
                if (this.shouldIgnoreError(request)) {
                    return of(new HttpResponse({ status: 204, body: null }));
                }
                this.redirectOn401(error);
                // 401 logges allerede som Warning i logHttpError — undgå dobbelt Error via ErrorHandler.
                const isUnauthorized = error?.status === 401;
                return throwError(
                    new DffError(error.message, {
                        innerError: error,
                        correlationId,
                        suppressLog: isUnauthorized
                    })
                );
            })
        );
    }

    private safe(fn: () => void): void {
        try {
            fn();
        } catch (error) {
            console.error("DffHttpInterceptor:", error);
        }
    }

    private logStart(correlationId: string, request: HttpRequest<any>): void {
        this.logService.logHttpStart(correlationId, request);
    }

    private logEnd(correlationId: string, startTime: Date, request: HttpRequest<any>, response: HttpEvent<any>): void {
        if (response instanceof HttpResponse) {
            const duration = new Date().getTime() - startTime.getTime();
            this.logService.logHttpEnd(correlationId, duration, request, response);
        }
    }

    private logError(correlationId: string, startTime: Date, request: HttpRequest<any>, error: any): void {
        const duration = new Date().getTime() - startTime.getTime();
        this.logService.logHttpError(correlationId, duration, request, error);
    }

    private redirectOn401(error: any): void {
        const status = error?.status ?? error?.innerError?.status;
        if (status !== 401) return;

        try {
            sessionStorage.removeItem("credentials");
            sessionStorage.removeItem("user");
        } catch {
            console.warn("Credentials kunne ikke fjernes fra sessionStorage");
        }

        const href = window.location.href;
        const configured = this.appShellLayout?.teknikerLoginRoute?.trim();
        const teknikerHash = isTeknikerContext() && configured ? (configured.startsWith("/") ? configured : `/${configured}`) : LOGIN_ROUTE;
        const target = buildAbsoluteLoginUrlFromHref(href, teknikerHash);

        const currentPath = (window.location.hash || "").replace(/^#/, "");
        if (!isLoginRoutePath(currentPath)) {
            this.showUnauthorizedAlertOnce();
            window.location.href = target;
        }
    }

    private showUnauthorizedAlertOnce(): void {
        if (EforsyningHttpInterceptor.hasShownUnauthorizedAlert) {
            return;
        }

        EforsyningHttpInterceptor.hasShownUnauthorizedAlert = true;
        window.alert("Din session er udløbet, og dine loginoplysninger er ikke længere aktive. Du bliver nu sendt til login.");
    }

    private shouldIgnoreError(request: HttpRequest<any>): boolean {
        if (!isTeknikerContext()) {
            return false;
        }

        const url = request?.url?.toLowerCase() || "";
        return url.includes("/api/notifications/documents");
    }

    private erSammeOrigin(url: string): boolean {
        if (!/^https?:\/\//i.test(url)) {
            return true;
        }
        try {
            if (typeof window === "undefined") {
                return false;
            }
            return new URL(url).origin === window.location.origin;
        } catch {
            return false;
        }
    }
}
